<template>
  <div class="camp-level-progress">
    <div class="progress-track" v-if="nodes.length">
      <div
        class="progress-node"
        v-for="(item, index) in nodes"
        :key="item.key"
        :class="[item.state, { 'current-level': item.isCurrent }]"
      >
        <div class="node-tag-slot">
          <span class="node-tag" v-if="item.tag">{{ item.tag }}</span>
          <i class="tag-arrow" :class="{ show: item.isCurrent }"></i>
        </div>
        <div class="node-rail">
          <i class="rail-line" :class="{ filled: index > 0 && index <= lastAchievedIndex, edge: index === 0 }"></i>
          <span class="node-dot">
            <img class="node-icon" :src="nodeIcon(item)" alt="">
          </span>
          <i class="rail-line" :class="{ filled: index < nodes.length - 1 && index < lastAchievedIndex, edge: index === nodes.length - 1 }"></i>
        </div>
        <div class="node-name">{{ item.name }}</div>
        <div class="node-desc" v-if="item.desc">{{ item.desc }}</div>
      </div>
    </div>
    <div class="progress-empty" v-else-if="emptyText">{{ emptyText }}</div>
  </div>
</template>

<script>
import { formatCampDate } from '../date'

const LEVEL_STATE = {
  1: 'done',
  2: 'urgent',
  3: 'warn',
  4: 'locked'
}

export default {
  name: 'CampLevelProgress',
  props: {
    levels: {
      type: Array,
      default () {
        return []
      }
    },
    member: {
      type: Object,
      default () {
        return {}
      }
    },
    emptyText: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      iconGreen: require('@/assets/images/researchCamp/ic_green_yes.svg'),
      iconRed: require('@/assets/images/researchCamp/ic_red_yes.svg'),
      iconLock: require('@/assets/images/researchCamp/ic_lock.svg')
    }
  },
  computed: {
    // 接口等级转成当前、目标节点
    nodes () {
      const member = this.member || {}
      return this.levels.map((item, index) => {
        const status = Number(item.status)
        const achieved = status === 1 || status === 2
        const isCurrent = member.currentLevel === item.pbiLevel
        const isTarget = member.targetLevel === item.pbiLevel
        const tags = []
        if (isCurrent) tags.push('当前')
        if (isTarget) tags.push('目标')
        let desc = ''
        let state = LEVEL_STATE[status] || 'locked'
        if (achieved) desc = formatCampDate(item.achievedAt)
        else if (item.overdue) {
          desc = '请尽快达成'
          state = 'late'
        } else if (item.suggestedDate) desc = `建议${formatCampDate(item.suggestedDate)}前达成`
        return {
          key: item.key || item.pbiLevel || index,
          tag: tags.join('/'),
          isCurrent,
          state,
          name: item.iepLevel || item.pbiLevel || '',
          desc,
          achieved
        }
      })
    },
    // 最后一个已达成节点。它左侧的连线用绿色
    lastAchievedIndex () {
      return this.nodes.reduce((last, item, index) => item.achieved ? index : last, -1)
    }
  },
  methods: {
    // 已达成用绿勾，超时达成用红勾，未达成用锁
    nodeIcon (item) {
      if (item.state === 'urgent') return this.iconRed
      if (item.achieved) return this.iconGreen
      return this.iconLock
    }
  }
}
</script>

<style lang="less" scoped>
@import '../camp.less';

.camp-level-progress {
  .camp-white-card();
  padding: 12px 6px 14px;
  border-radius: 8px;
  background: #fff url('../../../assets/images/researchCamp/bg_stage.png') no-repeat center / 100% 100%;

  .progress-track {
    display: flex;
    align-items: flex-start;
  }

  .progress-node {
    flex: 1;
    min-width: 0;
    text-align: center;
  }

  .node-tag-slot {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    height: 26px;
  }

  .node-tag {
    max-width: 100%;
    font-size: 10px;
    font-weight: 500;
    line-height: 18px;
    white-space: nowrap;
    color: @camp-muted;
  }

  .progress-node.current-level .node-tag {
    min-width: 30px;
    padding: 0 5px;
    box-sizing: border-box;
    border-radius: 9px;
    font-weight: 400;
    line-height: 18px;
    color: rgba(255, 255, 255, 0.9);
    background: @camp-green;
  }

  .progress-node.current-level.urgent .node-tag {
    background: @camp-red;
  }

  .tag-arrow {
    width: 0;
    height: 0;
    margin-top: 0;
    border-right: 3px solid transparent;
    border-bottom: 0;
    border-left: 3px solid transparent;
    border-top: 4px solid transparent;

    &.show {
      border-top-color: @camp-green;
    }
  }

  .progress-node.urgent .tag-arrow.show {
    border-top-color: @camp-red;
  }

  .node-rail {
    display: flex;
    align-items: center;
    height: 16px;
  }

  .rail-line {
    flex: 1;
    height: 2px;
    background: #d8d8d8;

    &.filled {
      background: @camp-green;
    }

    &.edge {
      background: transparent;
    }
  }

  // 节点之间的两段线各画在自己的格子里，大屏下 flex 取整会留出 1px 缝
  .progress-node:not(:last-child) .rail-line:last-child {
    margin-right: -1px;
  }

  .node-dot {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 12px;
    height: 12px;
  }

  .node-icon {
    display: block;
    width: 12px;
    height: 12px;
  }

  .node-name {
    margin-top: 4px;
    padding: 0 6px;
    font-size: 10px;
    font-weight: 500;
    line-height: 14px;
    word-break: break-word;
    overflow-wrap: break-word;
  }

  .done .node-name {
    color: @camp-green;
  }

  .urgent .node-name,
  .late .node-name {
    color: @camp-red;
  }

  .warn .node-name {
    color: @camp-orange;
  }

  .locked .node-name {
    color: #564C47;
  }

  .node-desc {
    margin-top: 1px;
    padding: 0 6px;
    font-size: 10px;
    font-weight: 400;
    line-height: 14px;
    word-break: break-word;
    overflow-wrap: break-word;
    opacity: 0.7;
  }

  .done .node-desc {
    color: @camp-green;
  }

  .urgent .node-desc,
  .late .node-desc {
    color: @camp-red;
  }

  .warn .node-desc {
    color: @camp-orange;
  }

  .locked .node-desc {
    color: #564C47;
  }

  .progress-empty {
    padding: 28px 0;
    text-align: center;
    font-size: 12px;
    color: @camp-sub;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/researchCamp/components/CampLevelProgress.vue